import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface Customer {
  id: string;
  organization_id: string;
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  notes: string | null;
  tags: string[] | null;
  total_visits: number | null;
  total_spent: number | null;
  loyalty_points: number | null;
  loyalty_tier: string | null;
  last_visit_at: string | null;
  created_at: string | null;
}

export function useCustomers(searchQuery?: string) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const customersQuery = useQuery({
    queryKey: ['customers', profile?.organization_id, searchQuery],
    queryFn: async () => {
      if (!profile?.organization_id) return [];

      let query = supabase
        .from('customers')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .order('last_visit_at', { ascending: false, nullsFirst: false })
        .limit(50);

      if (searchQuery && searchQuery.trim().length > 0) {
        const search = searchQuery.trim().toLowerCase();
        // Search by name, email, or phone
        query = query.or(
          `first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Customer[];
    },
    enabled: !!profile?.organization_id,
    staleTime: 30000, // 30 seconds
  });

  const createCustomer = useMutation({
    mutationFn: async (customer: {
      first_name: string;
      last_name?: string;
      email?: string;
      phone?: string;
      notes?: string;
    }) => {
      if (!profile?.organization_id) throw new Error('No organization');

      const { data, error } = await supabase
        .from('customers')
        .insert({
          organization_id: profile.organization_id,
          first_name: customer.first_name,
          last_name: customer.last_name || null,
          email: customer.email || null,
          phone: customer.phone || null,
          notes: customer.notes || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Customer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  return {
    customers: customersQuery.data || [],
    isLoading: customersQuery.isLoading,
    error: customersQuery.error,
    createCustomer,
  };
}
